import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, Clock, Phone, ShieldCheck, Link2Off, MessageSquare } from "lucide-react";
import { API } from "@/lib/api";
import { FULL_LOGO_URL } from "@/components/site/Logo";

// Offentlig kundeportal: sikkert, opgaveafgrænset link. Serveren kontrollerer adgang ved hver hentning.
export default function CustomerPortal() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let alive = true;
        const load = () => axios.get(`${API}/portal/${encodeURIComponent(token)}`)
            .then((r) => { if (alive) { setData(r.data); setError(""); } })
            .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Linket kunne ikke åbnes."); });
        load();
        const t = setInterval(load, 30000);
        return () => { alive = false; clearInterval(t); };
    }, [token]);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center px-4 py-8" data-testid="customer-portal">
            <img src={FULL_LOGO_URL} alt="MyServiceHelper" className="h-7 w-auto mb-6" />
            <div className="w-full max-w-md space-y-4">
                {error ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center shadow-sm" data-testid="portal-error">
                        <Link2Off className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="font-semibold text-slate-800">{error}</p>
                        <p className="text-sm text-slate-500 mt-1">Kontakt håndværkeren for et nyt link.</p>
                    </div>
                ) : !data ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : (
                    <>
                        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{data.company_name}</p>
                            <h1 className="text-xl font-bold text-slate-900 mt-1" data-testid="portal-task">{data.task}</h1>
                            {(data.date || data.time) && <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Clock className="w-4 h-4" />{[data.date, data.time].filter(Boolean).join(" · ")}</p>}
                            <ol className="mt-4 space-y-2" data-testid="portal-steps">
                                {data.steps.map((s) => (
                                    <li key={s.key} className="flex items-center gap-3 text-sm">
                                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${s.state === "done" ? "bg-emerald-500 text-white" : s.state === "current" ? "bg-blue-600 text-white ring-4 ring-blue-100" : "bg-slate-200 text-slate-400"}`}>{s.state === "done" ? <CheckCircle2 className="w-3.5 h-3.5" /> : ""}</span>
                                        <span className={s.state === "current" ? "font-bold text-blue-700" : s.state === "done" ? "text-emerald-700" : "text-slate-400"}>{s.label}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        {data.stage === "paa_vej" && (
                            <div className="bg-blue-600 text-white rounded-2xl p-5 shadow-sm" data-testid="portal-arrival">
                                <p className="text-xs font-bold uppercase tracking-wider text-blue-100">På vej</p>
                                <p className="text-lg font-bold mt-1">{data.employee_first_name ? `${data.employee_first_name} er på vej` : "Håndværkeren er på vej"}</p>
                                {data.arrival?.expected_at && <p className="text-blue-100 text-sm mt-1">Forventet ankomst {data.arrival.expected_at}{data.arrival.lead_minutes ? ` · du får besked ${data.arrival.lead_minutes} min. før` : ""}</p>}
                                {data.arrival?.notified && <p className="text-xs text-blue-100 mt-2 flex items-center gap-1"><Phone className="w-3.5 h-3.5" />Du er blevet kontaktet om ankomsten.</p>}
                            </div>
                        )}

                        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="portal-notes">
                            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" />Beskeder fra håndværkeren</h2>
                            {data.shared_notes.length === 0 ? <p className="text-sm text-slate-400 italic">Ingen delte beskeder.</p> : (
                                <ul className="space-y-2">
                                    {data.shared_notes.map((n, i) => <li key={i} className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-sm text-slate-800"><p>{n.text}</p>{n.time && <p className="text-xs text-slate-400 mt-1">{n.time}</p>}</li>)}
                                </ul>
                            )}
                        </div>

                        {data.company_phone && <a href={`tel:${data.company_phone}`} className="block text-center h-11 leading-[44px] rounded-xl bg-white border border-slate-200 text-blue-700 font-semibold" data-testid="portal-call">Ring til {data.company_name}</a>}
                        <p className="text-[11px] text-slate-400 text-center flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Sikkert link — gyldigt til {new Date(data.expires_at).toLocaleString("da-DK", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Copenhagen" })}</p>
                    </>
                )}
            </div>
        </div>
    );
}
